/**
 * An admin's member picker for one row (ISSUE-1288), after v1's
 * GroupMemberList sheet. Lists the pool's members from
 * `/strips/assignee-list` with each one's rows on this strip, and marks the
 * ones at the cap. "Nobody" empties the row. A member at the cap can't be
 * picked unless they already hold this row; the server's `isAtLimit` says
 * who that is, and the assign call checks the cap again.
 */
import { useStripsAssigneeList } from "@sportsmet/contest-sdk/private";
import { Button, Dialog, Radio, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import type { typeStripRowView, typeStripView } from "./stripBoardModel";
import {
  STRIP_ASSIGN_EMPTY,
  STRIP_ASSIGN_LIST_LABEL,
  STRIP_ASSIGN_NOBODY,
  STRIP_ASSIGN_NOBODY_HINT,
  STRIP_ASSIGN_SAVE_LABEL,
  STRIP_CANCEL_LABEL,
  stripAssignMemberHint,
  stripAssignTitle,
} from "./stripCardCopy";

const NOBODY = "__nobody__";

export type typeStripAssigneeSheetProps = {
  open: boolean;
  poolId: string;
  eventId: string;
  strip: typeStripView | null;
  row: typeStripRowView | null;
  isBusy: boolean;
  onClose: () => void;
  onAssign: (input: {
    stripNumber: number;
    row: number;
    userId: string | null;
  }) => Promise<boolean>;
};

export const StripAssigneeSheet = (
  props: typeStripAssigneeSheetProps,
): ReactNode => {
  const { strip, row } = props;
  const currentValue = row?.userId ?? NOBODY;
  const [value, setValue] = useState<string>(currentValue);

  useEffect(() => {
    setValue(currentValue);
  }, [currentValue, props.open]);

  const read = useStripsAssigneeList(
    {
      poolId: props.poolId,
      eventId: props.eventId,
      stripNumber: strip?.stripNumber ?? 1,
    },
    { enabled: props.open && strip !== null },
  );
  const members = read.data?.data?.members ?? [];

  if (strip === null || row === null) return null;

  async function save(): Promise<void> {
    if (strip === null || row === null) return;
    if (value === currentValue) {
      props.onClose();
      return;
    }
    const isDone = await props.onAssign({
      stripNumber: strip.stripNumber,
      row: row.row,
      userId: value === NOBODY ? null : value,
    });
    if (isDone) props.onClose();
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      title={stripAssignTitle({
        stripLabel: strip.label,
        rowLabel: row.rowLabel,
      })}
      autoFocus={false}
      footer={
        <>
          <Button variant="secondary" onClick={props.onClose}>
            {STRIP_CANCEL_LABEL}
          </Button>
          <Button
            variant="primary"
            data-testid="strip-assign-save"
            loading={props.isBusy}
            disabled={read.isPending}
            onClick={() => {
              void save();
            }}
          >
            {STRIP_ASSIGN_SAVE_LABEL}
          </Button>
        </>
      }
    >
      <div data-testid="strip-assignee-sheet" className="flex flex-col gap-3">
        {read.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton shape="card" />
            <Skeleton shape="card" />
          </div>
        ) : (
          <>
            <Radio
              label={STRIP_ASSIGN_LIST_LABEL}
              value={value}
              onValueChange={setValue}
              items={[
                {
                  value: NOBODY,
                  label: STRIP_ASSIGN_NOBODY,
                  description: STRIP_ASSIGN_NOBODY_HINT,
                },
                ...members.map((member) => ({
                  value: member.userId,
                  label: member.displayName,
                  description: stripAssignMemberHint({
                    spotCount: member.spotCount,
                    isAtLimit: member.isAtLimit,
                  }),
                  disabled: member.isAtLimit && member.userId !== row.userId,
                })),
              ]}
            />
            {members.length === 0 ? (
              <p className="text-caption text-ink-secondary">
                {STRIP_ASSIGN_EMPTY}
              </p>
            ) : null}
          </>
        )}
      </div>
    </Dialog>
  );
};
